import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, X, type LucideIcon } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/** Which card a filter belongs to: the green, the orange or the red one. */
export type QuickFilterGroup = "healthy" | "warning" | "attention";

export interface QuickFilter<TId extends string = string> {
  id: TId;
  group: QuickFilterGroup;
  icon: LucideIcon;
  iconClassName: string;
  labelKey: string;
}

interface QuickFilterCardsProps<TId extends string> {
  counts: Partial<Record<TId, number>>;
  filters: QuickFilter<TId>[];
  ariaLabel: string;
  value: TId | null;
  onChange: (value: TId | null) => void;
}

interface QuickFilterRowProps<TId extends string> {
  filter: QuickFilter<TId>;
  label: string;
  count: number;
  isActive: boolean;
  onToggle: () => void;
}

const GROUP_CARD_CLASS: Record<QuickFilterGroup, string> = {
  healthy: "border-success-200 bg-success-50",
  warning: "border-warning-200 bg-warning-50",
  attention: "border-danger-200 bg-danger-50",
};

const CARD_CLASS = "gap-0 rounded-lg py-3 shadow-sm";

const GROUPS = ["healthy", "warning", "attention"] as const;

const GROUP_ROW_CLASS: Record<QuickFilterGroup, { hover: string; active: string }> = {
  healthy: { hover: "hover:bg-success-100", active: "bg-success-200" },
  warning: { hover: "hover:bg-warning-100", active: "bg-warning-200" },
  attention: { hover: "hover:bg-danger-100", active: "bg-danger-200" },
};

const getGroups = <TId extends string>(filters: QuickFilter<TId>[]) =>
  GROUPS.filter(group => filters.some(filter => filter.group === group));

const getGridClass = (groupCount: number) =>
  cn("grid grid-cols-1 gap-4", groupCount === 3 ? "md:grid-cols-3" : "md:grid-cols-2");

/** Generic over the element, not over `QuickFilter`, so callers keep their own extra fields. */
export const getQuickFilter = <TFilter extends { id: string }>(
  filters: TFilter[],
  id: string | null
) => filters.find(quickFilter => quickFilter.id === id);

const QuickFilterRow = <TId extends string>({
  filter,
  label,
  count,
  isActive,
  onToggle,
}: QuickFilterRowProps<TId>) => {
  const Icon = filter.icon;
  const ActionIcon = isActive ? X : ChevronRight;

  return (
    <button
      type="button"
      aria-pressed={isActive}
      disabled={count === 0}
      onClick={onToggle}
      className={cn(
        "flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-60",
        isActive
          ? GROUP_ROW_CLASS[filter.group].active
          : cn(GROUP_ROW_CLASS[filter.group].hover, "disabled:hover:bg-transparent")
      )}
    >
      <span
        className={cn(
          "inline-flex size-7 shrink-0 items-center justify-center rounded-md",
          filter.iconClassName
        )}
      >
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="font-semibold text-foreground text-sm tabular-nums w-7 flex justify-center">
        {count}
      </span>
      <span className="text-foreground text-sm leading-5">{label}</span>
      <ActionIcon className="ml-auto size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </button>
  );
};

export const QuickFilterCards = <TId extends string>({
  counts,
  filters,
  ariaLabel,
  value,
  onChange,
}: QuickFilterCardsProps<TId>) => {
  const { t } = useTranslation();

  const rows = useMemo(
    () =>
      filters.map(filter => ({
        filter,
        count: counts[filter.id] ?? 0,
        label: t(filter.labelKey),
      })),
    [filters, counts, t]
  );

  const groups = getGroups(filters);

  return (
    <section aria-label={ariaLabel} className={getGridClass(groups.length)}>
      {groups.map(group => (
        <Card key={group} className={cn(CARD_CLASS, GROUP_CARD_CLASS[group])}>
          <CardContent className="flex flex-col gap-1 px-3">
            {rows
              .filter(row => row.filter.group === group)
              .map(row => (
                <QuickFilterRow
                  key={row.filter.id}
                  filter={row.filter}
                  label={row.label}
                  count={row.count}
                  isActive={value === row.filter.id}
                  onToggle={() => onChange(value === row.filter.id ? null : row.filter.id)}
                />
              ))}
          </CardContent>
        </Card>
      ))}
    </section>
  );
};

export const QuickFilterCardsSkeleton = <TId extends string>({
  filters,
}: {
  filters: QuickFilter<TId>[];
}) => (
  <section className={getGridClass(getGroups(filters).length)} aria-hidden="true">
    {getGroups(filters).map(group => (
      <Card key={group} className={cn(CARD_CLASS, GROUP_CARD_CLASS[group])}>
        <CardContent className="flex flex-col gap-y-4 p-3">
          {filters
            .filter(filter => filter.group === group)
            .map(filter => (
              <Skeleton key={filter.id} className="h-5 w-full py-3" />
            ))}
        </CardContent>
      </Card>
    ))}
  </section>
);
